import { toast } from "sonner";
import appsignal from "@/lib/appsignal";

// True for HTTP 4xx client errors, whether from an axios error or a fetch Response.
export function isClientError(err) {
  const status = err?.response?.status ?? err?.status;
  return typeof status === "number" && status >= 400 && status <= 499;
}

// Strip query/hash and replace ID-like path segments with `:id` so AppSignal
// can group by route shape rather than treating every distinct ID as a new
// error. Only collapses pure-numeric segments, UUIDs, and long opaque tokens
// — stable route segments with incidental digits (e.g. `v2`, `step-2`) are
// preserved.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_RE = /^\d+$/;
const LONG_TOKEN_RE = /^[A-Za-z0-9_-]{24,}$/;

function normalizeUrl(rawUrl) {
  if (!rawUrl) return "";
  let path = rawUrl.split("?")[0].split("#")[0];
  try {
    path = new URL(rawUrl, "http://x").pathname;
  } catch {}
  return path
    .split("/")
    .map((seg) => {
      if (!seg) return seg;
      if (NUMERIC_RE.test(seg) || UUID_RE.test(seg) || LONG_TOKEN_RE.test(seg)) {
        return ":id";
      }
      return seg;
    })
    .join("/");
}

function wrapAxiosError(err) {
  // Prefer the HTTP status (more granular: 500 vs 502 vs 503) when the
  // request reached the server; fall back to err.code (ERR_NETWORK,
  // ECONNABORTED, etc.) for connection-level failures.
  const code = err.response?.status || err.code || "UNKNOWN";
  const method = err.config?.method?.toUpperCase() || "REQUEST";
  const url = normalizeUrl(err.config?.url);
  const wrapped = new Error(`${method} ${url} (${code}) — ${err.message}`);
  wrapped.name = `AxiosError ${code}`;
  // Rewrite only the header line of the original stack so AppSignal's first
  // frame still points at the axios call site, but the name/message match
  // the wrapped error. Fall back to the freshly-created stack if the
  // original is missing.
  if (err.stack) {
    const lines = err.stack.split("\n");
    lines[0] = `${wrapped.name}: ${wrapped.message}`;
    wrapped.stack = lines.join("\n");
  }
  return wrapped;
}

// Surface 4xx client errors to the user via a toast; report everything else
// (5xx, network failures, timeouts) to AppSignal. 4xx errors are treated as
// expected user-facing failures and are never sent to AppSignal — if no
// `message` is provided, a 4xx is dropped silently. Callers that need a 4xx
// reported should report it themselves.
//
// Pass `force` to always show the toast regardless of status (e.g. admin-facing
// views where every failure should be visible). Non-4xx errors are still sent
// to AppSignal even when forced, so monitoring is preserved.
export function reportError(err, message, force = false) {
  const clientError = isClientError(err);
  if (message && (force || clientError)) {
    toast.error(message);
  }
  if (!clientError) {
    let toSend;
    if (err?.isAxiosError) {
      toSend = wrapAxiosError(err);
    } else if (err instanceof Error) {
      toSend = err;
    } else {
      toSend = new Error(String(err));
    }
    appsignal.sendError(toSend);
  }
}
